import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { api, logout, redirectToLogin } from './api/client';
import ResetPassword from './pages/ResetPassword';
import ReviewQueue from './pages/ReviewQueue';
import DecisionDetail from './pages/DecisionDetail';
import LibraryList from './pages/LibraryList';
import LibraryDetail from './pages/LibraryDetail';
import LibraryManage from './pages/LibraryManage';
import LibraryImport from './pages/LibraryImport';
import AnomalyList from './pages/AnomalyList';
import PipelineStatus from './pages/PipelineStatus';
import Admin from './pages/Admin';
import './App.css';

type Volunteer = { id: number; name: string; email: string; role: string; password_reset_required?: boolean };

function PrivateBetaPlaceholder({ volunteer }: { volunteer: Volunteer }) {
  return (
    <div className="private-beta">
      <div className="private-beta-card">
        <h1>Library Rankings</h1>
        <p className="private-beta-status">Private beta</p>
        <p>
          The Library Collection Rankings dashboard is still being built and
          isn't open to the broader Internet Sociology audience yet. We'll
          email when it goes live.
        </p>
        <p>
          You're signed in as <strong>{volunteer.email}</strong>.
          The rest of <a href="https://internetsociology.org/">Internet Sociology</a>
          {' '}is open to you in the meantime.
        </p>
        <p>
          <a href="https://internetsociology.org/">← Back to Internet Sociology</a>
          <span className="private-beta-sep"> · </span>
          <button onClick={logout} className="link-button">Sign out</button>
        </p>
      </div>
    </div>
  );
}

/** Resolve current auth state once at app start by calling /api/auth/me.
 *  The shared session cookie may have been set on a different subdomain
 *  (auth.is.org or socialarchive), so we can't rely on a localStorage
 *  marker — we have to ask the server. 401 → redirect to central login. */
function useBootstrapAuth(): { volunteer: Volunteer | null; loading: boolean } {
  const [volunteer, setVolunteer] = useState<Volunteer | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ data: Volunteer }>('/auth/me')
      .then(({ data }) => {
        localStorage.setItem('varilib_volunteer', JSON.stringify(data));
        setVolunteer(data);
      })
      .catch(() => { /* api client already redirected on 401; nothing to do */ })
      .finally(() => setLoading(false));
  }, []);

  return { volunteer, loading };
}

/** If the user logged in with a temporary password, gently steer them
 *  to the change-password page from anywhere in the app. */
function PasswordResetGate({ volunteer }: { volunteer: Volunteer }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (volunteer.password_reset_required && window.location.pathname !== '/reset-password') {
      navigate('/reset-password', { replace: true });
    }
  }, [volunteer, navigate]);
  return null;
}

function AppLayout({ volunteer }: { volunteer: Volunteer }) {
  return (
    <div className="app">
      <PasswordResetGate volunteer={volunteer} />
      <nav className="sidebar">
        <h2><a href="https://internetsociology.org" className="sidebar-brand"><img src="/logo-is-cutout.svg" alt="" className="sidebar-logo" /> Varilib</a></h2>
        <ul>
          <li><NavLink to="/review">Review Queue</NavLink></li>
          <li><NavLink to="/libraries">Libraries</NavLink></li>
          <li><NavLink to="/anomalies">Anomalies</NavLink></li>
          <li><NavLink to="/pipeline">Pipeline Status</NavLink></li>
          <li><NavLink to="/admin">Admin</NavLink></li>
        </ul>
        <div className="sidebar-footer">
          <span className="sidebar-user">{volunteer.name || 'User'}</span>
          <button onClick={logout} className="logout-btn">Log out</button>
        </div>
      </nav>
      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}

function App() {
  const { volunteer, loading } = useBootstrapAuth();

  if (loading) {
    return <div className="app-loading">Loading…</div>;
  }
  if (!volunteer) {
    // /api/auth/me returned 401 — the api client has already kicked
    // off a redirect to the central login app. Render a brief
    // placeholder while the navigation completes.
    redirectToLogin();
    return <div className="app-loading">Redirecting to sign in…</div>;
  }
  if (volunteer.role !== 'admin') {
    // Library Rankings is in private beta. Non-admin Internet Sociology
    // users are signed in (their cookie is good across the portfolio),
    // but varilib's API answers 403 for them and the frontend doesn't
    // bother trying to render a UI they can't use.
    return <PrivateBetaPlaceholder volunteer={volunteer} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppLayout volunteer={volunteer} />}>
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Navigate to="/review" replace />} />
          <Route path="/review" element={<ReviewQueue />} />
          <Route path="/review/:id" element={<DecisionDetail />} />
          <Route path="/libraries" element={<LibraryList />} />
          <Route path="/libraries/new" element={<LibraryManage />} />
          <Route path="/libraries/import" element={<LibraryImport />} />
          <Route path="/libraries/:id" element={<LibraryDetail />} />
          <Route path="/libraries/:id/edit" element={<LibraryManage />} />
          <Route path="/anomalies" element={<AnomalyList />} />
          <Route path="/pipeline" element={<PipelineStatus />} />
          <Route path="/admin" element={<Admin />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
